import type { ReactNode } from 'react'
import type { ComponentConfig, Config } from '@puckeditor/core'

// Puck merges this into every ComponentConfig (declaration merging via its
// ComponentConfigExtensions hook). `drawerIcon` shows in the component
// drawer and outline; see editor/componentIcons.tsx for builtin fallbacks.
declare module '@puckeditor/core' {
  interface ComponentConfigExtensions {
    drawerIcon?: ReactNode
  }
}
import { Button, ButtonProps } from './blocks/Button'
import { Flex, FlexProps } from './blocks/Flex'
import { Grid, GridProps } from './blocks/Grid'
import { Heading, HeadingProps } from './blocks/Heading'
import { Text, TextProps } from './blocks/Text'
import { Positioner, PositionerProps } from './blocks/Positioner'
import { PuckVideoEmbed, VideoEmbedProps } from './components/VideoEmbed'
import { PuckImage, ImageProps } from './components/Image'
import { PuckEmbedCode, EmbedCodeProps } from './components/EmbedCode'
import { PuckIcon, IconBlockProps } from './components/Icon'
import { PuckSvgIcon, SvgIconProps } from './components/SvgIcon'
import {
  PuckFullScreenBanner,
  FullScreenBannerProps,
} from './components/FullScreenBanner'
import {
  PuckAccordion,
  AccordionProps,
} from './components/Accordion'
import { Font, FontProps } from './blocks/Font'
import { Richtext, RichtextProps } from './blocks/Richtext'
import { PuckHeroSection, HeroSectionProps } from './components/HeroSection'
import { PuckBandSlider, BandSliderProps } from './components/BandSlider'
import { PuckBreak, BreakBlockProps } from './components/Break'
import { PuckCarousel, CarouselProps } from './components/Carousel'
import {
  PuckPrivacyRightsRequest,
  PrivacyRightsRequestProps,
} from './components/PrivacyRightsRequest'
import { withResponsive } from './responsive/withResponsive'

export type ThemeComponents = {
  Grid: GridProps
  Flex: FlexProps
  Heading: HeadingProps
  Text: TextProps
  Button: ButtonProps
  Positioner: PositionerProps
  VideoEmbed: VideoEmbedProps
  Image: ImageProps
  EmbedCode: EmbedCodeProps
  Icon: IconBlockProps
  SvgIcon: SvgIconProps
  FullScreenBanner: FullScreenBannerProps
  Accordion: AccordionProps
  Font: FontProps
  Richtext: RichtextProps
  HeroSection: HeroSectionProps
  BandSlider: BandSliderProps
  Break: BreakBlockProps
  Carousel: CarouselProps
  PrivacyRightsRequest: PrivacyRightsRequestProps
}

export type ThemeCustomFields = {
  colorPicker: {
    type: 'colorPicker'
    label: string
    /** Show a clear button so the color can be unset (fall back to defaults). */
    clearable?: boolean
    /** Allow an alpha channel — stores an `rgba(...)` string (e.g. transparent). */
    alpha?: boolean
  }
  fileUpload: {
    type: 'fileUpload'
    label: string
    /** MIME types or extensions, e.g. { 'image/*': ['.png', '.jpg'] } */
    accept?: Record<string, string[] | readonly string[]>
    /** Max file size in bytes */
    maxSize?: number
    /** Path to the file in the bucket */
    path?: string
  }
  datePicker: {
    type: 'datePicker'
    label: string
  }
  /** Dropdown of beaker2 PROMO_PAGE steps (BR-5419); value is the step id. */
  promoStepPicker: {
    type: 'promoStepPicker'
    label: string
  }
  boolean: {
    type: 'boolean'
    label: string
  }
}

export const THEME_CATEGORY_NAMES = ['layout', 'typography', 'media', 'actions', 'content', 'fonts'] as const
export type ThemeCategoryName = (typeof THEME_CATEGORY_NAMES)[number]

/**
 * Nested group inside a drawer category. Rendered as a collapsible
 * sub-section by ComponentDrawer. Puck itself only knows flat categories,
 * so subcategories are an app-level extension carried on the category
 * object (Puck ignores unknown keys at runtime).
 */
export type DrawerSubcategory = {
  components: string[]
  title?: string
  defaultExpanded?: boolean
}

export type DrawerCategory = {
  components?: string[]
  title?: string
  visible?: boolean
  defaultExpanded?: boolean
  subcategories?: Record<string, DrawerSubcategory>
}

export type ThemeCategories = {
  [K in ThemeCategoryName]: DrawerCategory & {
    components: Array<keyof ThemeComponents>
  }
}

export type ThemePuckConfig = Config<{
  components: ThemeComponents
  categories: typeof THEME_CATEGORY_NAMES
  fields: ThemeCustomFields
}>

export const themeComponentsCategories: ThemeCategories = {
  layout: {
    components: ['Grid', 'Flex', 'Positioner'],
  },
  typography: {
    components: ['Heading', 'Text', 'Richtext'],
  },
  media: {
    components: [
      'Image',
      'VideoEmbed',
      'EmbedCode',
      'Icon',
      'SvgIcon',
      'FullScreenBanner',
      'BandSlider',
    ],
  },
  actions: {
    components: ['Button'],
  },
  content: {
    components: [
      'Accordion',
      'HeroSection',
      'Break',
      'Carousel',
      'PrivacyRightsRequest',
    ],
  },
  fonts: {
    components: ['Font'],
  },
}

export const themeComponentsConfig = {
  Grid,
  Flex,
  Heading,
  Text,
  Button,
  Positioner,
  VideoEmbed: PuckVideoEmbed,
  Image: PuckImage,
  EmbedCode: PuckEmbedCode,
  Icon: PuckIcon,
  SvgIcon: PuckSvgIcon,
  FullScreenBanner: PuckFullScreenBanner,
  Accordion: PuckAccordion,
  Font,
  Richtext,
  HeroSection: PuckHeroSection,
  BandSlider: PuckBandSlider,
  Break: PuckBreak,
  Carousel: PuckCarousel,
  PrivacyRightsRequest: PuckPrivacyRightsRequest,
}

/**
 * Factory that returns a typed Puck Config with theme components and
 * categories pre-merged. Sites pass their extra components, categories,
 * fields, and root config. Internal cast hides Puck's ConfigInternal
 * shape mismatch so consumers get a clean typed return.
 */
/**
 * Type a component config strictly with its Props. Use inside the
 * `components` map of createPuckConfig when you need precise typing on
 * `render`, `resolveFields`, etc - beyond the loose `ComponentConfig<any>`
 * the createPuckConfig signature gives by default.
 *
 * Second generic registers extra custom field types (e.g. `iconPicker`).
 * Defaults to ThemeCustomFields (colorPicker, fileUpload, datePicker,
 * boolean) - pass a wider type when the component uses site-specific
 * field types.
 */
export function defineComponent<
  P extends Record<string, any>,
  F extends Record<string, { type: string }> = ThemeCustomFields,
>(
  config: ComponentConfig<{ props: P; fields: F }>,
): ComponentConfig<{ props: P; fields: F }> {
  return config
}

export function createPuckConfig<C extends Config<any>>(opts: {
  components?: Record<string, ComponentConfig<any>>
  categories?: Record<string, DrawerCategory>
  root: NonNullable<C['root']>
}): C {
  // Inject the responsive `display` field into every component so any block can
  // be shown/hidden or re-displayed per breakpoint (BR-5403).
  const components = Object.fromEntries(
    Object.entries({
      ...themeComponentsConfig,
      ...(opts.components ?? {}),
    }).map(([name, component]) => [name, withResponsive(component)]),
  )
  return {
    components,
    categories: { ...themeComponentsCategories, ...(opts.categories ?? {}) },
    root: opts.root,
  } as unknown as C
}
